"use strict";
/**
 * Size ceilings for a user-created skill's instruction text (`skillText`).
 *
 * A skill is loaded verbatim into the agent's context whenever it is attached to a chat, so an
 * oversized skill eats the context window and can slow down — or outright break — a conversation.
 * (ENG-3779: a customer's chat broke after two very large skills were added.) These thresholds are
 * the single source of truth shared by the API — which enforces the hard cap on every write path —
 * and the web app, which warns the user live while they edit.
 *
 * Measured in characters: cheap to compute in both the browser and the backend, and a good enough
 * proxy for token cost without pulling in a tokenizer.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SKILL_TEXT_MAX_CHARS = exports.SKILL_TEXT_WARN_CHARS = void 0;
exports.estimateSkillTokens = estimateSkillTokens;
exports.assessSkillSize = assessSkillSize;
/** Above this, saving is still allowed but the user is warned the skill is getting large. */
exports.SKILL_TEXT_WARN_CHARS = 30000;
/** Hard cap. A skill's instructions may not exceed this — larger skills are rejected on save. */
exports.SKILL_TEXT_MAX_CHARS = 100000;
/** Rough token estimate (~4 characters per token) to give users a sense of the context cost. */
function estimateSkillTokens(skillText) {
    return Math.ceil(skillText.length / 4);
}
function formatCount(n) {
    return n.toLocaleString('en-US');
}
/**
 * Classifies a skill's instruction text as `ok`, `warning` (large but savable), or `exceeded`
 * (over the hard cap, must be reduced before saving) and builds the matching user-facing message.
 */
function assessSkillSize(skillText) {
    const chars = skillText.length;
    const estimatedTokens = estimateSkillTokens(skillText);
    if (chars > exports.SKILL_TEXT_MAX_CHARS) {
        return {
            estimatedTokens,
            level: 'exceeded',
            message: `This skill is too large to save (${formatCount(chars)} characters, ~${formatCount(estimatedTokens)} tokens; ` +
                `the limit is ${formatCount(exports.SKILL_TEXT_MAX_CHARS)} characters). Oversized skills are loaded into a chat's ` +
                `context window, which slows chats down and can break them. Split it into smaller, focused skills or trim ` +
                `it before saving.`,
        };
    }
    if (chars > exports.SKILL_TEXT_WARN_CHARS) {
        return {
            estimatedTokens,
            level: 'warning',
            message: `This skill is large (${formatCount(chars)} characters, ~${formatCount(estimatedTokens)} tokens). Large ` +
                `skills take up the chat's context window and can slow down or degrade performance. Consider splitting it ` +
                `into smaller, focused skills or trimming it before saving.`,
        };
    }
    return { estimatedTokens, level: 'ok', message: null };
}
